import { createServerFn } from "@tanstack/react-start";
import { and, eq, isNull } from "drizzle-orm";
import { nanoid } from "nanoid";
import { z } from "zod";
import {
  citationNotes,
  projectCitations,
  projectScreeningSettings,
  screeningDecisions,
} from "~/lib/db/schema";

const citationNotesSchema = z.object({
  projectId: z.string(),
  citationId: z.string(),
});

const updateNotesSchema = z.object({
  projectId: z.string(),
  citationId: z.string(),
  notes: z.string(),
});

const upsertNoteSchema = z.object({
  projectId: z.string(),
  citationId: z.string(),
  content: z.string(),
});

const deleteNoteSchema = z.object({
  projectId: z.string(),
  citationId: z.string(),
});

export interface CitationNoteUser {
  id: string;
  name: string;
  image: string | null;
}

export interface CitationNoteEntry {
  id: string;
  userId: string;
  userName: string;
  userImage: string | null;
  content: string;
  createdAt: string;
  updatedAt: string;
}

async function getCitationInProject(
  db: typeof import("~/lib/db").db,
  projectId: string,
  citationId: string,
) {
  const citation = await db.query.projectCitations.findFirst({
    where: and(
      eq(projectCitations.projectId, projectId),
      eq(projectCitations.id, citationId),
    ),
    columns: {
      id: true,
      notes: true,
      screeningPhase: true,
    },
  });

  if (!citation) {
    throw new Error("Citation not found");
  }

  return citation;
}

async function shouldHidePeerNotes(
  db: typeof import("~/lib/db").db,
  projectId: string,
  citationId: string,
  userId: string,
) {
  const citation = await getCitationInProject(db, projectId, citationId);
  const settings = await db.query.projectScreeningSettings.findFirst({
    where: eq(projectScreeningSettings.projectId, projectId),
    columns: {
      screeningMode: true,
      blindReviewers: true,
    },
  });

  if (
    !settings ||
    settings.screeningMode !== "dual" ||
    !settings.blindReviewers
  ) {
    return false;
  }

  const myDecision = await db.query.screeningDecisions.findFirst({
    where: and(
      eq(screeningDecisions.projectCitationId, citationId),
      eq(screeningDecisions.reviewerId, userId),
      eq(screeningDecisions.phase, citation.screeningPhase),
      isNull(screeningDecisions.deletedAt),
    ),
    columns: { id: true },
  });

  return !myDecision;
}

/**
 * Get notes for a citation (legacy - from screening decision or project citation)
 */
export const $getCitationNotes = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => citationNotesSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);
    const citation = await getCitationInProject(db, data.projectId, data.citationId);

    // First check if there's a screening decision with notes
    const decision = await db.query.screeningDecisions.findFirst({
      where: and(
        eq(screeningDecisions.projectCitationId, data.citationId),
        eq(screeningDecisions.reviewerId, user.id),
        eq(screeningDecisions.phase, citation.screeningPhase),
        isNull(screeningDecisions.deletedAt),
      ),
    });

    if (decision?.notes) {
      return { notes: decision.notes };
    }

    return { notes: citation?.notes ?? "" };
  });

/**
 * Update notes for a citation (legacy)
 */
export const $updateCitationNotes = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => updateNotesSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);
    await getCitationInProject(db, data.projectId, data.citationId);

    // Update notes on the project citation
    await db
      .update(projectCitations)
      .set({ notes: data.notes || null })
      .where(eq(projectCitations.id, data.citationId));

    return { success: true };
  });

/**
 * Get all users' notes for a citation
 */
export const $getAllCitationNotes = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => citationNotesSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);
    const hidePeerNotes = await shouldHidePeerNotes(
      db,
      data.projectId,
      data.citationId,
      user.id,
    );

    const notes = await db.query.citationNotes.findMany({
      where: eq(citationNotes.projectCitationId, data.citationId),
      with: {
        user: {
          columns: { id: true, name: true, image: true },
        },
      },
      orderBy: (cn, { asc }) => [asc(cn.createdAt)],
    });

    const visibleNotes = hidePeerNotes
      ? notes.filter((note) => note.userId === user.id)
      : notes;

    return {
      currentUserId: user.id,
      notes: visibleNotes.map((n) => ({
        id: n.id,
        userId: n.userId,
        userName: n.user.name,
        userImage: n.user.image,
        content: n.content,
        createdAt: n.createdAt.toISOString(),
        updatedAt: n.updatedAt.toISOString(),
      })) satisfies CitationNoteEntry[],
    };
  });

/**
 * Upsert current user's note for a citation
 */
export const $upsertCitationNote = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => upsertNoteSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);
    await getCitationInProject(db, data.projectId, data.citationId);

    const content = data.content.trim();

    // If empty, delete the note
    if (!content) {
      await db
        .delete(citationNotes)
        .where(
          and(
            eq(citationNotes.projectCitationId, data.citationId),
            eq(citationNotes.userId, user.id),
          ),
        );
      return { success: true };
    }

    // Check if exists
    const existing = await db.query.citationNotes.findFirst({
      where: and(
        eq(citationNotes.projectCitationId, data.citationId),
        eq(citationNotes.userId, user.id),
      ),
    });

    if (existing) {
      await db
        .update(citationNotes)
        .set({ content })
        .where(eq(citationNotes.id, existing.id));
    } else {
      await db.insert(citationNotes).values({
        id: nanoid(),
        projectCitationId: data.citationId,
        userId: user.id,
        content,
      });
    }

    return { success: true };
  });

/**
 * Delete current user's note for a citation
 */
export const $deleteCitationNote = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => deleteNoteSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);
    await getCitationInProject(db, data.projectId, data.citationId);

    await db
      .delete(citationNotes)
      .where(
        and(
          eq(citationNotes.projectCitationId, data.citationId),
          eq(citationNotes.userId, user.id),
        ),
      );

    return { success: true };
  });
